import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { alpha } from "@mui/material/styles";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

import type {
  BarChartIntent,
  ChartDatum,
  LineChartIntent,
  PieChartIntent,
  TableIntent,
  VizIntent,
} from "../../types/multiChatbot";
import { SEARCE_COLORS } from "../../theme";

const PALETTE = [
  SEARCE_COLORS.primary,
  SEARCE_COLORS.success,
  SEARCE_COLORS.error,
  SEARCE_COLORS.secondary,
  SEARCE_COLORS.warning,
  SEARCE_COLORS.info,
  SEARCE_COLORS.orange,
  SEARCE_COLORS.purple,
];

function isTable(v: VizIntent): v is TableIntent {
  return v.kind === "table";
}
function isBar(v: VizIntent): v is BarChartIntent {
  return v.kind === "bar";
}
function isLine(v: VizIntent): v is LineChartIntent {
  return v.kind === "line";
}
function isPie(v: VizIntent): v is PieChartIntent {
  return v.kind === "pie";
}

export function formatVizCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "number") {
    return Number.isInteger(v) ? String(v) : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}

/** Pivot flat `ChartDatum[]` into one row per `x` with one column per `series`. */
export function pivotChartDataBySeries(data: ChartDatum[]): {
  rows: Array<Record<string, string | number>>;
  seriesKeys: string[];
} {
  const xOrder: Array<string | number> = [];
  const seen = new Set<string>();
  const seriesSet = new Set<string>();
  const rowsByX = new Map<string, Record<string, string | number>>();
  for (const d of data) {
    const xKey = String(d.x);
    if (!seen.has(xKey)) {
      seen.add(xKey);
      xOrder.push(d.x);
      rowsByX.set(xKey, { x: d.x });
    }
    const series = d.series && d.series.length > 0 ? d.series : "value";
    seriesSet.add(series);
    const row = rowsByX.get(xKey)!;
    row[series] = d.y;
  }
  return {
    rows: xOrder.map((x) => rowsByX.get(String(x))!),
    seriesKeys: Array.from(seriesSet),
  };
}

function TableBlock({ viz }: { viz: TableIntent }) {
  const cols = Array.isArray(viz.columns) ? viz.columns : [];
  const rows = Array.isArray(viz.rows) ? viz.rows : [];
  return (
    <Paper variant="outlined" sx={{ mt: 1.5, overflow: "hidden" }}>
      <Box sx={{ px: 1.25, py: 1, borderBottom: 1, borderColor: "divider", bgcolor: alpha(SEARCE_COLORS.primary, 0.06) }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {viz.title}
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c) => (
                <TableCell key={c} sx={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, ri) => (
              <TableRow key={ri}>
                {cols.map((c) => (
                  <TableCell key={c} sx={{ fontSize: "0.75rem", verticalAlign: "top" }}>
                    {formatVizCell(row[c])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ChartShell({ title, children }: { title: string; children: React.ReactElement }) {
  return (
    <Paper variant="outlined" sx={{ mt: 1.5, p: 1.25 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          {React.cloneElement(children, {
            margin: { top: 8, right: 8, left: 8, bottom: 28 },
          })}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

function BarBlock({ viz }: { viz: BarChartIntent }) {
  const { rows, seriesKeys } = pivotChartDataBySeries(viz.data || []);
  return (
    <ChartShell title={viz.title}>
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke={SEARCE_COLORS.border} />
        <XAxis
          dataKey="x"
          label={{ value: viz.x_label, position: "insideBottom", offset: -6 }}
          tick={{ fontSize: 11 }}
        />
        <YAxis label={{ value: viz.y_label, angle: -90, position: "insideLeft" }} tick={{ fontSize: 11 }} />
        <Tooltip />
        {seriesKeys.length > 1 && <Legend />}
        {seriesKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={PALETTE[i % PALETTE.length]} />
        ))}
      </BarChart>
    </ChartShell>
  );
}

function LineBlock({ viz }: { viz: LineChartIntent }) {
  const { rows, seriesKeys } = pivotChartDataBySeries(viz.data || []);
  return (
    <ChartShell title={viz.title}>
      <LineChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke={SEARCE_COLORS.border} />
        <XAxis
          dataKey="x"
          label={{ value: viz.x_label, position: "insideBottom", offset: -6 }}
          tick={{ fontSize: 11 }}
        />
        <YAxis label={{ value: viz.y_label, angle: -90, position: "insideLeft" }} tick={{ fontSize: 11 }} />
        <Tooltip />
        {seriesKeys.length > 1 && <Legend />}
        {seriesKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={PALETTE[i % PALETTE.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartShell>
  );
}

function PieBlock({ viz }: { viz: PieChartIntent }) {
  const data = (viz.data || []).map((d) => ({ name: String(d.x), value: Number(d.y) || 0 }));
  const innerRadius = viz.donut ? 60 : 0;
  return (
    <ChartShell title={viz.title}>
      <PieChart>
        <Tooltip />
        <Legend />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={100}
          innerRadius={innerRadius}
          label
          isAnimationActive={false}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
      </PieChart>
    </ChartShell>
  );
}

export type MultiChatbotVisualizationsProps = {
  items: VizIntent[] | undefined;
};

export function MultiChatbotVisualizations({ items }: MultiChatbotVisualizationsProps) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Visualizations
      </Typography>
      {list.map((viz, i) => {
        if (isTable(viz)) return <TableBlock key={`viz-${i}`} viz={viz} />;
        if (isBar(viz)) return <BarBlock key={`viz-${i}`} viz={viz} />;
        if (isLine(viz)) return <LineBlock key={`viz-${i}`} viz={viz} />;
        if (isPie(viz)) return <PieBlock key={`viz-${i}`} viz={viz} />;
        return null;
      })}
    </Box>
  );
}
